import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Formik } from 'formik'

import { Box } from '@dhl-ui/components'

import { links } from '~/AppRoutes'
import { postData } from '~/api/admin-api'
import { useNotificationMessage } from '~/common'
import { getErrorCode } from '~/common/util/axiosUtil'
import { BackButton } from '~/temp-ds-component/BackButton'

import {
  CancelDialog,
  ServicepointVendorForm,
  useCreateServicepointVendorValidationSchema,
} from '~/modules/servicepointvendors/components'
import {
  initialServicepointVendor,
  convertToServicepointVendorDto,
  type ServicepointVendor,
} from '~/modules/servicepointvendors/model'

export const AddServicepointVendorPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { showSuccess, showError } = useNotificationMessage()
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false)

  const validationSchema = useCreateServicepointVendorValidationSchema()

  const onSubmit = async (servicepoint: ServicepointVendor) => {
    const servicepointDto = convertToServicepointVendorDto(servicepoint)
    try {
      await postData(`vendors`, 'POST', servicepointDto)
      showSuccess(t('admin.servicePointVendorsPage.addServicePointVendorSuccess'))
      navigate(links.servicePointVendorsLink())
    } catch (error) {
      const errorCode = getErrorCode(error)
      switch (errorCode) {
        case 'EXTERNAL_ID_NOT_UNIQUE':
          showError(t('admin.servicePointVendorsPage.servicePointVendorNotUniqueError'))
          break
        case 'EXTERNAL_ID_INVALID':
          showError(t('admin.servicePointVendorsPage.servicePointVendorInvalidError'))
          break
        default:
          showError(t('admin.servicePointVendorsPage.addServicePointVendorError'))
      }
    }
  }

  const onBack = (dirty: boolean) => (dirty ? setCancelDialogOpen(true) : navigate(links.servicePointVendorsLink()))

  return (
    <>
      <Formik
        initialValues={initialServicepointVendor}
        validateOnMount
        validateOnChange
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ dirty }) => (
          <>
            <Box p={2}>
              <Box display="flex" justifyContent="space-between" pb={2}>
                <BackButton onClick={() => onBack(dirty)}>
                  {t('admin.servicePointVendorsPage.servicePointVendorsForm.backButtonTitle')}
                </BackButton>
              </Box>
              <ServicepointVendorForm />
            </Box>
          </>
        )}
      </Formik>
      <CancelDialog open={cancelDialogOpen} onCancel={() => setCancelDialogOpen(false)} />
    </>
  )
}
